import React, { useCallback } from 'react'

import BaseBookmarkButton from '../../Common/BaseBookmarkButton'
import SquareBookmarkButton from '../../Common/SquareBookmarkButton'

import { track } from 'analytics/analytics'
import { saveMapSearchResultInListEvent } from 'analytics/snowplow/events'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import noop from 'lib/function/noop'
import { clearRecentlySavedTripId } from 'storage/recentSavedTrip'
import { getGoogleExploreItemsPlaceDetails } from 'tripPlanner/api/places'
import {
  HOTEL_TYPE,
  determineItemType,
} from 'tripPlanner/components/TripMaps/MapSearch'
import { useTripId } from 'tripPlanner/contexts/TripContext'
import {
  useCreateTripItem,
  useDeleteTripItem,
  useTrip,
} from 'tripPlanner/hooks/api'
import useBookmarkSnackbarHandlers from 'tripPlanner/hooks/bookmarks/useSavedItemSnackbarHandlers'
import { useTripPermissions } from 'tripPlanner/hooks/useTripPermissions'
import { setCurrentSectionId } from 'tripPlanner/reducers/actions'
import {
  selectTripPlannerTemplateId,
  selectTripPlannerTemplateItemId,
} from 'tripPlanner/selectors'
import { FullTrip } from 'tripPlanner/types/common'
import { getMatchingSavedItem } from 'tripPlanner/utils/explore'
import { createExploreItemBookmarkPayload } from 'tripPlanner/utils/itemConversions/bookmarks/exploreItem'
import { itemSectionId } from 'tripPlanner/utils/itemSection'

export interface Props {
  exploreItemId: string
  variant: 'text' | 'icon'
  tripId?: string
  onSnackbarViewClick?: () => void
  onSave?: () => void
  onDelete?: () => void
}

function SearchBookmarkButtonFromPlaceId({
  exploreItemId,
  variant,
  tripId: tripIdProp,
  onSnackbarViewClick = noop,
  onSave = noop,
  onDelete = noop,
}: Props) {
  const contextTripId = useTripId()
  const tripId = tripIdProp ?? contextTripId
  const { data: trip } = useTrip({ tripId })
  const { canEdit } = useTripPermissions(tripId)

  const savedItem = trip ? getMatchingSavedItem(trip, exploreItemId) : undefined
  const isSaved = !!savedItem

  const {
    showSaveSuccessSnackbar,
    showSaveErrorSnackbar,
    showRemoveSuccessSnackbar,
    showRemoveErrorSnackbar,
  } = useBookmarkSnackbarHandlers()

  const { mutate: deleteBookmark, isLoading: isDeleting } = useDeleteTripItem({
    onSuccess: (_res) => {
      onDelete()
      showRemoveSuccessSnackbar(trip!.id, trip!.name, onSnackbarViewClick)
    },
    onError: (_res, _vars) => {
      showRemoveErrorSnackbar(trip!.name)
    },
  })

  const dispatch = useAppDispatch()

  const { mutateAsync: createItem, isLoading: isCreating } = useCreateTripItem({
    onSuccess: (item) => {
      onSave()
      dispatch(setCurrentSectionId(itemSectionId(item.id)))
    },
    onError: (e) => {
      console.error(e)
      showSaveErrorSnackbar(trip!.name)

      // Clear the recently saved trip ID in case it errored because the trip was deleted
      clearRecentlySavedTripId()
    },
  })

  const templateId = useAppSelector(selectTripPlannerTemplateId)
  const templateItemId = useAppSelector(selectTripPlannerTemplateItemId)

  const createTripItem = useCallback(
    async (trip: FullTrip) => {
      try {
        const data = await getGoogleExploreItemsPlaceDetails([exploreItemId])
        const exploreItem = data.items[0]

        const type =
          determineItemType(exploreItem.types) === HOTEL_TYPE
            ? 'HOTEL'
            : 'ATTRACTION'

        const payload = createExploreItemBookmarkPayload({
          exploreItem,
          templateId,
          templateItemId,
          type,
        })
        const createdItem = await createItem({
          tripId,
          tripItem: payload,
        })
        showSaveSuccessSnackbar(
          trip.id,
          trip.name,
          createdItem.id,
          undefined,
          undefined,
          onSnackbarViewClick,
        )

        track(
          'click-save-map-search-result-in-list',
          saveMapSearchResultInListEvent(),
        )
      } catch (e) {
        // Do nothing - failure case is handled by the hook's onError handler
      }
    },
    [
      exploreItemId,
      templateId,
      templateItemId,
      createItem,
      tripId,
      showSaveSuccessSnackbar,
      onSnackbarViewClick,
    ],
  )

  const onButtonClick = useCallback(async () => {
    if (!trip) {
      return
    }
    if (savedItem) {
      deleteBookmark({
        tripId: trip.id,
        tripItemId: savedItem.id,
      })
    } else {
      createTripItem(trip)
    }
  }, [trip, savedItem, deleteBookmark, createTripItem])

  if (!canEdit || !tripId) {
    return null
  }

  const Button = variant === 'icon' ? BaseBookmarkButton : SquareBookmarkButton

  return (
    <Button
      isSaved={isSaved}
      onClick={onButtonClick}
      isProcessing={isDeleting || isCreating}
    />
  )
}

export default SearchBookmarkButtonFromPlaceId
